/**
 * paid-1 (Hormonal Imprint) view-model + builder.
 *
 * Maps the LIVE api shape (`score.analysis` eye/skin params + `scanResults.hd_*`
 * Perfect Corp metrics + `score.perfectCorpAllScore`) onto the design's panel.
 *
 * All shown numbers are real (Perfect Corp metrics). ⚠️ Several severity
 * thresholds are unvalidated + the cohort benchmark is blocked — tracked in
 * OPEN_ISSUES.md (OI-4..OI-8, OI-13). Flagged inline.
 */
import { FullResultsData } from '../../../services/result.service';
import { Band, ComparisonRailVM, FaceAnalysisVM, Tone } from '../../results-vm';
import {
  CohortAverages,
  imprintBand,
} from '../../results-placeholders';

export interface SeverityValue {
  labelKey: string; // i18n key (reuses results.biomarkers.severity.*)
  color: string;
}
export interface ZoneRow {
  labelKey: string;
  sev: SeverityValue;
}
export interface ZoneCardVM {
  eyebrowKey: string;
  accent: string;
  /** Darker shade of accent for the small eyebrow text (AA 4.5:1 on white). */
  accentText: string;
  status: { labelKey: string; bg: string; color: string };
  rows: ZoneRow[];
}
export interface MetricBarVM {
  labelKey: string;
  score: number;
  avg: number | null;
  color: string;
}
export interface RegionRowVM {
  labelKey: string;
  valueKey: string; // i18n key for the verbal level
  color: string;
}
export interface EyeDetailVM {
  titleKey: string;
  sevKey: string;
  sevBg: string;
  sevColor: string;
  descKey: string;
}
export interface WrinkleZoneVM {
  labelKey: string;
  descKey: string;
  pct: number;
  sevKey: string;
  color: string;
}

export interface ImprintPanelVM {
  score: number | null;
  band: Band;
  /** band-specific hero description key (lower score = milder signals) */
  heroCopy1Key: string;
  comparison: ComparisonRailVM;
  skinZone: ZoneCardVM;
  eyeZone: ZoneCardVM;
  // deep-dive
  vitality: { score: number; avg: number | null };
  skinMetrics: MetricBarVM[];
  oiliness: RegionRowVM[];
  dryness: RegionRowVM[];
  pores: RegionRowVM[];
  eyeDetail: EyeDetailVM[];
  wrinkleZones: WrinkleZoneVM[];
  /** marked-selfie + Perfect Corp metric carousel (handoff #21); null if no selfie. */
  faceAnalysis: FaceAnalysisVM | null;
}

// ---- face-analysis card (#21) ----
// The hd_* keys that mean "this scan has a usable Perfect Corp face analysis".
const FACE_HD_KEYS = [
  'hd_acne', 'hd_pore', 'hd_wrinkle', 'hd_moisture', 'hd_redness',
  'hd_age_spot', 'hd_dark_circle', 'hd_firmness', 'hd_texture',
];

// Reuse the SHARED skin-filter-viewer (the real Perfect Corp mask overlay also
// shown in the pre-results preview) instead of a duplicate decorative-dot
// overlay. We just hand it the same inputs the preview uses: the selfie photo,
// the Perfect Corp results (`scanResults`), and the mask-image map.
function buildFaceAnalysisVM(full: FullResultsData): FaceAnalysisVM | null {
  const raw = full?.userInfo?.images?.front;
  const sr = full?.scanResults || {};
  if (!raw) return null;
  // Only show the card when there's at least one Perfect Corp metric to render.
  if (!FACE_HD_KEYS.some((k) => sr[k])) return null;
  const photoUrl = /^(data:|https?:)/.test(raw) ? raw : `data:image/jpeg;base64,${raw}`;
  return { photoUrl, filterData: sr, maskImages: full?.maskImages || {} };
}

// ---- colour helpers (match design tokens) ----
const GREEN = '#1a7a4a';
const AMBER = '#b37400';
const RED = '#bf4800';
const NAVY = '#1a1a2e';
const GREY = '#888888';

function sevColor(key: string): string {
  if (!key) return GREY;
  if (key.endsWith('mild') || key.endsWith('none')) return GREEN;
  if (key.endsWith('moderate')) return AMBER;
  if (key.endsWith('severe') || key.endsWith('pronounced')) return RED;
  return NAVY;
}
const SEV = {
  none: 'results.biomarkers.severity.mild',
  mild: 'results.biomarkers.severity.mild',
  moderate: 'results.biomarkers.severity.moderate',
  severe: 'results.biomarkers.severity.severe',
};
/** higher-is-better score → severity bucket (⚠️ unvalidated cutoffs — OI-4) */
function sevFromHigherBetter(score: number): { key: string; color: string } {
  if (score >= 70) return { key: SEV.mild, color: GREEN };
  if (score >= 40) return { key: SEV.moderate, color: AMBER };
  return { key: SEV.severe, color: RED };
}
/** higher = worse (e.g. dryness %) → severity (⚠️ unvalidated cutoffs — OI-5) */
function sevFromHigherWorse(pct: number): { key: string; color: string } {
  if (pct < 50) return { key: SEV.mild, color: GREEN };
  if (pct < 65) return { key: SEV.moderate, color: AMBER };
  return { key: SEV.severe, color: RED };
}
function barColor(score: number): string {
  if (score >= 80) return '#1fa864';
  if (score >= 60) return '#f59d00';
  return '#e05a6e';
}
/** hero copy variant, keyed to the imprint band (thresholds MIRROR BAND_CONFIG.imprint:
 *  wordfirst sec 3 opening values 34 / 67, so word + hero copy + 3-level bar agree). */
function imprintCopyKey(score: number): string {
  if (score < 34) return 'results.hi.heroCopy1.mild';
  if (score < 67) return 'results.hi.heroCopy1.moderate';
  return 'results.hi.heroCopy1.pronounced';
}
function uiScore(metric: any): number {
  const v = metric?.ui_score ?? metric?.whole?.ui_score;
  return typeof v === 'number' ? Math.round(v) : 0;
}

function findParam(analysis: any[], type: string, catSuffix: string): any {
  const block = (analysis || []).find((a) => a?.type === type);
  return (block?.parameters || []).find((p: string & any) =>
    (p?.category || '').endsWith(catSuffix)
  );
}
function eyeBlockSeverity(analysis: any[]): string {
  return (analysis || []).find((a) => a?.type === 'eye_analysis')?.severity || '';
}

function statusFromWorst(sevKeys: string[]): { labelKey: string; bg: string; color: string } {
  const has = (s: string) => sevKeys.some((k) => k.endsWith(s));
  if (has('severe') || has('pronounced'))
    return { labelKey: 'results.hi.status.attention', bg: '#fff0e6', color: RED };
  if (has('moderate'))
    return { labelKey: 'results.hi.status.watch', bg: '#fff4e0', color: AMBER };
  return { labelKey: 'results.hi.status.good', bg: '#e8f7ef', color: GREEN };
}

/** "20%, results.biomarkers.oilinessLevels.mediumShine" → levelKey */
function oilinessLevelKey(entry: string | undefined): string {
  if (!entry) return '';
  const parts = String(entry).split(',');
  return (parts[1] || '').trim();
}
function pctFromEntry(entry: string | number | undefined): number {
  if (typeof entry === 'number') return entry;
  const m = String(entry ?? '').match(/([\d.]+)\s*%/);
  return m ? parseFloat(m[1]) : 0;
}

export function buildImprintVM(
  full: FullResultsData,
  imprintScore: number | null,
  cohort: CohortAverages | null,
  showComparisons = true
): ImprintPanelVM {
  const score = full?.score || {};
  const analysis: any[] = Array.isArray(score.analysis) ? score.analysis : [];
  const sr = full?.scanResults || {};

  // ---- comparison rail ----
  // Imprint cohort average is now real (all-sample, server-computed from the
  // same Perfect Corp overall score the per-user imprint uses) — OI-13 unblock.
  const comparison: ComparisonRailVM = {
    youValue: imprintScore ?? 0,
    avgValue: cohort?.imprint ?? 0,
    max: 100,
    headKey: 'results.hi.cmpHeadSample',
    startKey: 'results.hi.cmpStart',
    endKey: 'results.hi.cmpEnd',
    avgNoteKey: 'results.hi.sampleAvg',
    avgNoteParams: { avg: cohort?.imprint ?? 0 },
    hasAvg: showComparisons && cohort?.imprint != null,
  };

  // ---- skin zone (sensitivity / pigmentation / dryness) ----
  const pSens = findParam(analysis, 'skin_analysis', 'skinCategories.skinSensitivity');
  const pPig = findParam(analysis, 'skin_analysis', 'skinCategories.pigmentation');
  const pDryOily = findParam(analysis, 'skin_analysis', 'skinCategories.dryOilySkin');

  // sensitivity_level: higher = more sensitive (⚠️ unvalidated direction/cutoffs — OI-4)
  const sensLvl = Number(pSens?.sensitivity_level) || 0;
  const sensSev =
    sensLvl < 34 ? { key: SEV.mild, color: GREEN } : sensLvl < 67 ? { key: SEV.moderate, color: AMBER } : { key: SEV.severe, color: RED };
  // melanin_score: higher = better
  const melanin = Number(pPig?.melanin_score) || 0;
  const pigSev = sevFromHigherBetter(melanin);
  // dryness: average of zone % (higher = drier)
  const dryInfo = (pDryOily?.additional_info || []).find((x: any) =>
    (x?.type || '').endsWith('drynessCoverageAndIntensity')
  );
  const dryPcts = ['forehead', 'left_cheek', 'right_cheek']
    .map((z) => pctFromEntry(dryInfo?.[z]))
    .filter((n) => n > 0);
  const dryAvg = dryPcts.length ? dryPcts.reduce((a, b) => a + b, 0) / dryPcts.length : 0;
  const drySev = sevFromHigherWorse(dryAvg);

  const skinRows: ZoneRow[] = [
    { labelKey: 'results.hi.skinZone.sensitivity', sev: { labelKey: sensSev.key, color: sensSev.color } },
    { labelKey: 'results.hi.skinZone.pigmentation', sev: { labelKey: pigSev.key, color: pigSev.color } },
    { labelKey: 'results.hi.skinZone.dryness', sev: { labelKey: drySev.key, color: drySev.color } },
  ];
  const skinZone: ZoneCardVM = {
    eyebrowKey: 'results.hi.skinZone.title',
    accent: '#3a9e95',
    accentText: '#30827b',
    status: statusFromWorst([sensSev.key, pigSev.key, drySev.key]),
    rows: skinRows,
  };

  // ---- eye zone (dark circles / fine lines / puffiness) ----
  const eDark = findParam(analysis, 'eye_analysis', 'eyeCategories.darkCircle');
  const eFine = findParam(analysis, 'eye_analysis', 'eyeCategories.eyeFinelines');
  const ePouch = findParam(analysis, 'eye_analysis', 'eyeCategories.eyePouch');
  const eDeep = findParam(analysis, 'eye_analysis', 'eyeCategories.underEyeDeepWrinkles');
  const sevOf = (p: any): string => p?.detected_types?.[0]?.severity || p?.severity || SEV.mild;
  const eyeRows: ZoneRow[] = [
    { labelKey: 'results.hi.eyeZone.darkCircles', sev: { labelKey: sevOf(eDark), color: sevColor(sevOf(eDark)) } },
    { labelKey: 'results.hi.eyeZone.fineLines', sev: { labelKey: sevOf(eFine), color: sevColor(sevOf(eFine)) } },
    { labelKey: 'results.hi.eyeZone.puffiness', sev: { labelKey: sevOf(ePouch), color: sevColor(sevOf(ePouch)) } },
  ];
  const eyeStatusSev = eyeBlockSeverity(analysis);
  const eyeZone: ZoneCardVM = {
    eyebrowKey: 'results.hi.eyeZone.title',
    accent: '#f59d00',
    accentText: '#a46900',
    status: statusFromWorst([eyeStatusSev, sevOf(eDark), sevOf(eFine)]),
    rows: eyeRows,
  };

  // ---- deep-dive: vitality + skin metric bars ----
  const vitalityScore = Math.round(Number(sr?.all?.score) || Number(score.perfectCorpAllScore) || 0);
  const sm = showComparisons ? cohort?.skinMetric ?? null : null;
  const skinMetrics: MetricBarVM[] = [
    { labelKey: 'results.hi.metrics.pores', score: uiScore(sr.hd_pore), avg: sm?.pores ?? null, color: '' },
    { labelKey: 'results.hi.metrics.texture', score: uiScore(sr.hd_texture), avg: sm?.texture ?? null, color: '' },
    { labelKey: 'results.hi.metrics.hydration', score: uiScore(sr.hd_moisture), avg: sm?.hydration ?? null, color: '' },
    { labelKey: 'results.hi.metrics.pigmentation', score: melanin, avg: sm?.pigmentation ?? null, color: '' },
  ].map((m) => ({ ...m, color: barColor(m.score) }));

  // ---- region breakdowns ----
  const oilInfo = (pDryOily?.additional_info || []).find((x: any) =>
    (x?.type || '').endsWith('oilinessCoverageAndIntensity')
  );
  const oiliness: RegionRowVM[] = [
    { labelKey: 'results.hi.regions.tZone', key: 't_zone' },
    { labelKey: 'results.hi.regions.cheeks', key: 'left_cheek' },
    { labelKey: 'results.hi.regions.chin', key: 'chin' },
  ].map((r) => {
    const lvl = oilinessLevelKey(oilInfo?.[r.key]);
    return { labelKey: r.labelKey, valueKey: lvl || 'results.biomarkers.oilinessLevels.noShine', color: lvl.endsWith('severeOiling') ? RED : lvl.endsWith('mediumShine') ? AMBER : NAVY };
  });
  const dryness: RegionRowVM[] = [
    { labelKey: 'results.hi.regions.forehead', z: 'forehead' },
    { labelKey: 'results.hi.regions.leftCheek', z: 'left_cheek' },
    { labelKey: 'results.hi.regions.rightCheek', z: 'right_cheek' },
  ].map((r) => {
    const s = sevFromHigherWorse(pctFromEntry(dryInfo?.[r.z]));
    return { labelKey: r.labelKey, valueKey: s.key, color: s.color };
  });
  // pores by zone: enlarged-pore counts (higher = more pores = worse) ⚠️ unvalidated cutoffs — OI-6
  const poreInfo = (pDryOily?.additional_info || []).find((x: any) =>
    (x?.type || '').endsWith('detectedEnlargedPores')
  );
  const poreLvl = (n: number) =>
    n <= 12 ? { key: 'results.hi.poreLevels.faint', color: GREY } : n <= 22 ? { key: 'results.hi.poreLevels.light', color: NAVY } : { key: 'results.hi.poreLevels.medium', color: AMBER };
  const pores: RegionRowVM[] = [
    { labelKey: 'results.hi.regions.forehead', z: 'forehead' },
    { labelKey: 'results.hi.regions.leftCheek', z: 'left_cheek' },
    { labelKey: 'results.hi.regions.rightCheek', z: 'right_cheek' },
    { labelKey: 'results.hi.regions.chin', z: 'chin' },
  ].map((r) => {
    const lvl = poreLvl(Number(poreInfo?.[r.z]) || 0);
    return { labelKey: r.labelKey, valueKey: lvl.key, color: lvl.color };
  });

  // ---- eye detail (4 items) ----
  const eyeDetailFrom = (p: any, titleKey: string): EyeDetailVM => {
    const k = sevOf(p);
    return {
      titleKey,
      sevKey: k,
      sevBg: sevColor(k) === GREEN ? '#edfff5' : sevColor(k) === AMBER ? '#fff4e0' : '#fff0e6',
      sevColor: sevColor(k),
      descKey: p?.detected_types?.[0]?.content || p?.content || '',
    };
  };
  const eyeDetail: EyeDetailVM[] = [
    eyeDetailFrom(eDark, 'results.hi.eyeDetail.darkCircles'),
    eyeDetailFrom(eFine, 'results.hi.eyeDetail.fineLines'),
    eyeDetailFrom(eDeep, 'results.hi.eyeDetail.deepWrinkles'),
    eyeDetailFrom(ePouch, 'results.hi.eyeDetail.puffiness'),
  ];

  // ---- wrinkle zone map (6 zones; pct = 100 - ui_score, higher = more wrinkles) ----
  // ⚠️ severity cutoffs (60/35) unvalidated — OPEN_ISSUES.md OI-7
  // The bar fill (pct) and the chip (sevKey/color) BOTH derive from the final
  // pct, so they can never disagree with each other (legend stays true).
  const w = sr.hd_wrinkle || {};
  // The eye-area zones share their subject with the eye-zone OBSERVATIONS
  // ("מה נצפה באזור העיניים"), which read severity from eye_analysis. If we
  // computed the periocular/crow's-feet bars purely from hd_wrinkle.ui_score
  // they could read "קלה" while the observation reads "חמור" — the two sections
  // contradicting on the same area (BUG-IMAGE-WRINKLE-SEVERITY-DESYNC). Floor
  // the eye-zone bars to the observed eye severity so both sections agree and
  // the bridging sentence holds.
  const sevRank = (k: string): number =>
    !k ? 0 : k.endsWith('severe') || k.endsWith('pronounced') ? 3 : k.endsWith('moderate') ? 2 : 1;
  const eyeObservedSevKey = [sevOf(eFine), sevOf(eDeep)].reduce(
    (worst, k) => (sevRank(k) > sevRank(worst) ? k : worst),
    '',
  );
  // Min pct that lands in the same severity bucket (thresholds 60/35 below).
  const sevPctFloor = (k: string): number =>
    sevRank(k) >= 3 ? 70 : sevRank(k) === 2 ? 45 : 0;
  const zone = (
    key: string,
    labelKey: string,
    descKey: string,
    floorPct = 0,
  ): WrinkleZoneVM => {
    const ui = uiScore(w[key]);
    const pct = Math.max(0, Math.min(100, Math.max(100 - ui, floorPct)));
    const sev = pct >= 60 ? { key: 'results.biomarkers.wrinkleSeverity.pronounced', color: '#c44a00' } : pct >= 35 ? { key: 'results.biomarkers.wrinkleSeverity.moderate', color: '#f59d00' } : { key: 'results.biomarkers.wrinkleSeverity.mild', color: '#1fa864' };
    return { labelKey, descKey, pct, sevKey: sev.key, color: sev.color };
  };
  const eyeFloor = sevPctFloor(eyeObservedSevKey);
  const wrinkleZones: WrinkleZoneVM[] = [
    zone('periocular', 'results.hi.wrinkleZones.periocular.label', 'results.hi.wrinkleZones.periocular.desc', eyeFloor),
    zone('crowfeet', 'results.hi.wrinkleZones.crowfeet.label', 'results.hi.wrinkleZones.crowfeet.desc', eyeFloor),
    zone('nasolabial', 'results.hi.wrinkleZones.nasolabial.label', 'results.hi.wrinkleZones.nasolabial.desc'),
    zone('forehead', 'results.hi.wrinkleZones.forehead.label', 'results.hi.wrinkleZones.forehead.desc'),
    zone('glabellar', 'results.hi.wrinkleZones.glabellar.label', 'results.hi.wrinkleZones.glabellar.desc'),
    zone('marionette', 'results.hi.wrinkleZones.marionette.label', 'results.hi.wrinkleZones.marionette.desc'),
  ];

  return {
    score: imprintScore,
    band: imprintScore != null ? imprintBand(imprintScore) : imprintBand(0),
    heroCopy1Key:
      imprintScore != null
        ? imprintCopyKey(imprintScore)
        : 'results.hi.heroCopy1.moderate',
    comparison,
    skinZone,
    eyeZone,
    vitality: { score: vitalityScore, avg: showComparisons ? cohort?.vitality ?? null : null },
    skinMetrics,
    oiliness,
    dryness,
    pores,
    eyeDetail,
    wrinkleZones,
    faceAnalysis: buildFaceAnalysisVM(full),
  };
}
